/**
 * Utilitário de CNPJ com suporte ao formato ALFANUMÉRICO
 * (Nota Técnica Conjunta 2025.001 / IN RFB 2.229/2024, vigência jul/2026).
 *
 * Regras:
 *  - CNPJ continua com 14 caracteres.
 *  - Os 12 primeiros caracteres podem conter letras maiúsculas (A-Z) e dígitos.
 *  - Os 2 últimos (dígitos verificadores) permanecem numéricos.
 *  - Máscara: XX.XXX.XXX/XXXX-XX
 *  - DV: módulo 11 onde cada caractere vale (código ASCII - 48):
 *    '0'..'9' => 0..9, 'A'..'Z' => 17..42.
 *
 * IMPORTANTE: não usar replace(/\D/g, '') em CNPJ — apaga as letras.
 * Use clean().
 *
 * API compatível com a lib `cpf-cnpj-validator` (objeto `cnpj`): expõe
 * isValid() e format(), permitindo substituição direta.
 */

const PESOS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/** Remove a máscara preservando letras e dígitos; normaliza para maiúsculas. */
export function clean(cnpj) {
    if (cnpj === null || cnpj === undefined) {
        return '';
    }
    return String(cnpj).replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Valor do caractere no módulo 11: código ASCII - 48. */
function valor(ch) {
    return ch.charCodeAt(0) - 48;
}

function calcularDigito(base) {
    let soma = 0;
    const offset = PESOS.length - base.length;
    for (let i = 0; i < base.length; i++) {
        soma += valor(base.charAt(i)) * PESOS[offset + i];
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
}

/** Aplica a máscara XX.XXX.XXX/XXXX-XX. Se não tiver 14 chars, retorna limpo. */
export function format(cnpj) {
    const limpo = clean(cnpj);
    if (limpo.length !== 14) {
        return limpo;
    }
    return `${limpo.slice(0, 2)}.${limpo.slice(2, 5)}.${limpo.slice(5, 8)}/${limpo.slice(8, 12)}-${limpo.slice(12, 14)}`;
}

/** Valida CNPJ numérico ou alfanumérico (formato + dígitos verificadores). */
export function isValid(cnpj) {
    const limpo = clean(cnpj);
    if (limpo.length !== 14 || !/^[0-9A-Z]{12}[0-9]{2}$/.test(limpo)) {
        return false;
    }
    // rejeita sequência toda igual (ex.: 00000000000000)
    if (/^(.)\1{13}$/.test(limpo)) {
        return false;
    }
    const base = limpo.slice(0, 12);
    const dv1 = calcularDigito(base);
    const dv2 = calcularDigito(base + dv1);
    return Number(limpo.charAt(12)) === dv1 && Number(limpo.charAt(13)) === dv2;
}

/**
 * Aplica máscara de CPF ou CNPJ a um valor digitado, durante a digitação.
 * - Até 11 caracteres limpos: trata como CPF (somente dígitos) → 000.000.000-00
 * - Acima de 11: trata como CNPJ alfanumérico (A-Z nos 12 primeiros, dígitos
 *   nos 2 do DV) → XX.XXX.XXX/XXXX-XX
 * Retorna o valor já mascarado para uso em inputs controlados.
 */
export function maskCpfCnpj(value) {
    if (value === null || value === undefined) {
        return '';
    }
    let limpo = clean(value);
    const ehCpf = limpo.length <= 11;

    if (ehCpf) {
        // CPF: somente dígitos, máximo 11
        limpo = limpo.replace(/[^0-9]/g, '').slice(0, 11);
        return limpo
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    // CNPJ alfanumérico: 12 primeiros [0-9A-Z], 2 últimos só dígitos
    limpo = limpo.slice(0, 14);
    const base = limpo.slice(0, 12);
    const dv = limpo.slice(12).replace(/[^0-9]/g, '');
    let resultado = base
        .replace(/^([0-9A-Z]{2})([0-9A-Z])/, '$1.$2')
        .replace(/^([0-9A-Z]{2}\.[0-9A-Z]{3})([0-9A-Z])/, '$1.$2')
        .replace(/^([0-9A-Z]{2}\.[0-9A-Z]{3}\.[0-9A-Z]{3})([0-9A-Z])/, '$1/$2');
    if (dv.length > 0) {
        resultado = resultado + '-' + dv;
    }
    return resultado;
}

/** Objeto compatível com `import { cnpj } from 'cpf-cnpj-validator'`. */
export const cnpj = { isValid, format, clean };

export default cnpj;
